// app/src/lib/procurement/priceAlerts.js — % change, contract overage, shrinkflation
// (US-2.7, US-2.8, US-2.9). Called from the OCR save flow for every confirmed line item.

import { db } from '../db.js';
import { requireUserId, cents, isoToday } from './util.js';
import { latestFor } from './priceBook.js';
import { activeContract } from './contracts.js';

// Thresholds per §12 Alert Catalog:
//   increase ≥5%  → info     (price_spike informational)
//   increase >15% → warning  (price_spike)
//   increase >30% → danger   (price_spike_major)
//   decrease >10% → info     (price_drop)  ← asymmetric vs increase
const THRESHOLD_INFO     = 0.05;
const THRESHOLD_WARNING  = 0.15;
const THRESHOLD_DANGER   = 0.30;
const THRESHOLD_DECREASE = 0.10;
const SHRINKFLATION_PRICE_TOLERANCE = 0.02;

/**
 * Run all alert checks for a single observed line item price. Persists alerts
 * to the alerts table; returns the alert summary for the caller (e.g. push).
 *
 * @param {{ vendorId:string, itemId:string, vendorName:string, itemName:string, observedPrice:number, observedPackSize?:string, effectiveDate?:string, invoiceId?:string }} obs
 */
export async function checkForAlerts(obs) {
  const userId = await requireUserId();
  if (!obs.vendorId || !obs.itemId) return [];
  const observedCents = cents(obs.observedPrice);
  const effective = obs.effectiveDate || isoToday();
  const alerts = [];

  const prior = await latestFor(obs.vendorId, obs.itemId);

  // Same-vendor % change.
  if (prior && prior.priceCents > 0) {
    const delta = (observedCents - prior.priceCents) / prior.priceCents;
    const sev = severityFor(delta);
    if (sev) {
      alerts.push(await addAlert(userId, {
        kind: delta >= 0 ? 'PRICE_INCREASE' : 'PRICE_DECREASE',
        severity: sev,
        title: deltaTitle(obs.itemName, delta),
        body: `${obs.vendorName}: ${formatCents(prior.priceCents)} → ${formatCents(observedCents)} on ${effective}`,
        meta: {
          itemId: obs.itemId, vendorId: obs.vendorId,
          previousPriceCents: prior.priceCents, newPriceCents: observedCents,
          deltaPct: delta, invoiceId: obs.invoiceId,
        },
      }));
    }

    // Shrinkflation: price within ±2% but pack size changed.
    const priceTolerance = Math.abs((observedCents - prior.priceCents) / prior.priceCents);
    if (priceTolerance <= SHRINKFLATION_PRICE_TOLERANCE && prior.packSize && obs.observedPackSize && prior.packSize !== obs.observedPackSize) {
      alerts.push(await addAlert(userId, {
        kind: 'SHRINKFLATION',
        severity: 'warning',
        title: `${obs.itemName} pack changed: ${prior.packSize} → ${obs.observedPackSize}`,
        body: `${obs.vendorName} kept price at ~${formatCents(observedCents)} but reduced pack size.`,
        meta: {
          itemId: obs.itemId, vendorId: obs.vendorId,
          previousPackSize: prior.packSize, newPackSize: obs.observedPackSize,
        },
      }));
    }
  }

  // Contract overage.
  const contract = await activeContract(obs.vendorId, obs.itemId, effective);
  if (contract && contract.agreedPriceCents > 0 && observedCents > contract.agreedPriceCents) {
    const over = (observedCents - contract.agreedPriceCents) / contract.agreedPriceCents;
    alerts.push(await addAlert(userId, {
      kind: 'CONTRACT_OVERAGE',
      severity: over > 0.10 ? 'critical' : 'warning',
      title: `${obs.vendorName} billed over contract on ${obs.itemName}`,
      body: `Charged ${formatCents(observedCents)} vs agreed ${formatCents(contract.agreedPriceCents)} (${(over * 100).toFixed(1)}% over).`,
      meta: {
        itemId: obs.itemId, vendorId: obs.vendorId, contractId: contract.contractId,
        agreedPriceCents: contract.agreedPriceCents, observedPriceCents: observedCents,
      },
    }));
  }

  return alerts;
}

/** Periodic check (e.g. daily): alert 30 days before contract expiry. */
export async function checkContractExpiries() {
  const userId = await requireUserId();
  const today = isoToday();
  const threshold = (() => { const d = new Date(); d.setUTCDate(d.getUTCDate() + 30); return d.toISOString().slice(0, 10); })();
  const due = await db.vendorContracts
    .where('[userId+effectiveThrough]')
    .between([userId, today], [userId, threshold], true, true)
    .toArray();
  const alerts = [];
  for (const c of due) {
    // Don't double-add if already alerted today for this contract.
    const exists = await db.alerts.where('userId').equals(userId)
      .filter((a) => a.kind === 'CONTRACT_EXPIRY' && a.meta?.contractId === c.contractId)
      .first();
    if (exists) continue;
    const vendor = await db.vendors.get(c.vendorId);
    const item   = await db.items.get(c.itemId);
    alerts.push(await addAlert(userId, {
      kind: 'CONTRACT_EXPIRY',
      severity: 'info', // §12 catalog: contract_expiry is info-level.
      title: `Contract expires ${c.effectiveThrough}`,
      body: `${vendor?.name || 'Vendor'} · ${item?.name || 'item'} contract ends in ${daysUntil(c.effectiveThrough)} days.`,
      meta: { contractId: c.contractId, vendorId: c.vendorId, itemId: c.itemId },
    }));
  }
  return alerts;
}

// ── internal ──────────────────────────────────────────────────────────────

function severityFor(delta) {
  // Asymmetric thresholds: small decreases are not noisy; only ≥10% drops fire.
  if (delta >= THRESHOLD_DANGER)  return 'critical';
  if (delta >= THRESHOLD_WARNING) return 'warning';
  if (delta >= THRESHOLD_INFO)    return 'info';
  if (delta <= -THRESHOLD_DECREASE) return 'info';
  return null;
}

function deltaTitle(itemName, delta) {
  const pct = (Math.abs(delta) * 100).toFixed(0);
  return delta >= 0 ? `${itemName} up ${pct}%` : `${itemName} down ${pct}%`;
}

function formatCents(c) {
  return `$${((c || 0) / 100).toFixed(2)}`;
}

function daysUntil(iso) {
  return Math.max(0, Math.round((new Date(iso) - new Date()) / (24 * 60 * 60 * 1000)));
}

async function addAlert(userId, { kind, severity, title, body, meta }) {
  const alert = {
    alertId: crypto.randomUUID(),
    userId, severity, kind, title, body,
    createdAt: Date.now(),
    dismissed: 0,
    meta,
  };
  await db.alerts.add(alert);
  return alert;
}
